import * as R from 'ramda';
import {
    emptyElement,
    createElement,
    updateElementAttribute,
    setElementAttribute,
    setElementHtml,
    addElementClass,
    removeElementClass,
} from './dom.js';

const HOSTNAME = 'https://dashtours.otpusk.com';

// DOM
const getGroupContainer = (groupId) => document.querySelector(`[data-type="dashtours-container"][data-group-id="${groupId}"]`);
const emptyGroupContainer = (groupId, viewType) => () => R.pipe(
    getGroupContainer,
    emptyElement,
    setElementAttribute('data-view-type', viewType),
)(groupId);
const loadResource = (name, type, attrs) => {
    let resource = document.querySelector(`[data-resource-name="${name}"`);

    if (!resource) {
        resource = createElement(type, R.mergeRight({ 'data-resource-code': name }, attrs));

        switch (type) {
        case 'link':
            document.head.append(resource);
            break;
        default:
            document.body.append(resource);
            break;
        }
    }
};
const loadGroupViewTypeStyles = (viewType) => () => {
    const url = `${HOSTNAME}/static/css/view.${viewType}.css`;

    loadResource(`dashtours-style-${viewType}`, 'link', {
        rel: 'stylesheet',
        href: url,
    });
};

// Order
const loadOrderResources = (actionType) => () => {
    if (actionType === 'order') {
        loadResource('dashtours-order-styles', 'link', {
            rel: 'stylesheet',
            href: 'https://export.otpusk.com/jsapi/order.min.css',
        });
        loadResource('dashtours-order-styles-icons', 'link', {
            rel: 'stylesheet',
            href: 'https://export.otpusk.com/os/templates/css/font-awesome.css',
        });
        loadResource('dashtours-order-script', 'script', {
            src: 'https://export.otpusk.com/jsapi/order.hot.min.js',
        });
    }
};

const openOrderDialog = (el, token, countryName, offerId) => {
    const getElAttr = R.propOr('', R.__, el.dataset);
    window.otpHotOrder.showForm(el, token, {
        open($e) {
            window.noMail = true;
            $e.addClass('os-order-hot-form');
            $e.find('.os-order-top-part').hide();
        },
        render($e) {
            $e.find('.os-order-input-country select')
                .empty()
                .append(createElement('option', { value: getElAttr('countryName'), selected: true }));
        },
        before_sent(query) {
            query.offerId = getElAttr('offerId');
        },
    });
};

// Group rendering
const createTourElement = (settings) => ([tourId, searchKey]) => {
    const prefixKeys = R.compose(R.fromPairs, R.map(([n, v]) => [`data-${n}`, v]), R.toPairs);
    const el = createElement('div', {
        id: `tour-${tourId}`,
        class: 'dashtours__tour',
        ...prefixKeys(settings),
    });

    el.style.display = 'none';

    getGroupContainer(settings['group-id']).appendChild(el);

    return [settings['group-id'], tourId, searchKey, el];
};

const createViewMoreButton = (settings) => (tours) => {
    const limit = settings['view-limit'];

    if (limit >= tours.length) {
        return 0;
    }

    const container = getGroupContainer(settings['group-id']);
    const button = createElement('a', { class: 'dashtours__view-all-button' });

    const style = createElement('style', { id: `dashtours-temp-${settings['group-id']}` });
    const tourElementSelector = `body [data-type="dashtours-container"][data-group-id="${settings['group-id']}"] > .dashtours__tour`;
    style.innerText = `${tourElementSelector} { display: none !important; }`;
    style.innerText += `${tourElementSelector}:nth-child(-n + ${limit}) { display:block !important; }`;
    document.head.append(style);

    button.addEventListener('click', () => {
        button.remove();
        style.remove();
    });

    container.appendChild(button);

    return 0;
};

const fetchTour = R.curry((settings, [groupId, tourId, searchKey, tourEl]) => {
    const loaderEl = createElement('div', { class: 'dashtours__loading' });

    if (!tourEl.childNodes.length) {
        tourEl.append(loaderEl);
    }

    return fetch(`${HOSTNAME}/groups/${groupId}/${tourId}/${searchKey}.html?lang=${settings.language}`)
        .then(renderTour(settings, groupId, tourId, searchKey, tourEl));
});

function renderTour(settings, groupId, tourId, searchKey, tourEl) {
    return (response) => {
        const retryAfter = (timeout, f, ...args) => setTimeout(() => f(...args), timeout);
        const partialResponse = response.status === 206;
        const emptyResponse = response.status === 204;
        const renderResponse = R.pipe(
            setElementHtml(tourEl),
            tuneTourAction(settings),
        );

        if (partialResponse) {
            retryAfter(1000, fetchTour(settings), [groupId, tourId, searchKey, tourEl]);
        } else if (emptyResponse) {
            tourEl.remove();
        } else {
            response.text().then(renderResponse);
        }

        return tourEl;
    };
}

const tuneTourAction = (settings) => (tourEl) => {
    const links = tourEl.querySelectorAll('[data-type="offer-url"]');

    switch (settings['action-type']) {
    case 'order':
        const clearHref = updateElementAttribute('href', R.always(''));
        const assignClickListener = (el) => {
            el.addEventListener('click', (e) => {
                openOrderDialog(el, settings['access-token']);
                e.preventDefault();
            });
            return el;
        };

        R.map(R.compose(assignClickListener, clearHref), links);
        break;
    case 'details':
        const searchUrl = settings['search-page-url'];

        R.map(
            updateElementAttribute('href', R.concat(searchUrl)),
            links,
        );
        break;
    }

    return tourEl;
};

function parseGroupHeaders(response) {
    const headers = [];

    for (const entry of response.headers.entries()) {
        headers.push(entry);
    }

    return R.pipe(
        R.filter(R.compose(R.startsWith('x-settings-'), R.nth(0))),
        R.map(R.over(R.lensIndex(0), R.replace('x-settings-', ''))),
        R.fromPairs,
    )(headers);
}

const renderGroup = (groupId, settings) => {
    R.pipe(
        (id) => `${HOSTNAME}/groups/concept/${id}?access_token=${settings['access-token']}`,
        fetch,
        R.andThen(R.applySpec({
            settings: R.compose(R.mergeLeft(settings), parseGroupHeaders),
            tours: R.invoker(0, 'json'),
        })),
        R.andThen(({ settings, tours }) => R.andThen(R.pipe(
            R.tap(emptyGroupContainer(settings['group-id'], settings['view-type'])),
            R.tap(loadGroupViewTypeStyles(settings['view-type'])),
            R.tap(loadOrderResources(settings['action-type'])),
            R.toPairs,
            R.map(createTourElement(settings)),
            R.tap(createViewMoreButton(settings)),
            R.map(fetchTour(settings)),
        ))(tours)),
    )(groupId);
};

window.__DASHTOURS__ = {
    renderGroup,
};
